import { z } from 'zod/v4';

import { analyticsSettingsSchemaLegacy } from '../../../common/schemas/legacy/analytics';
import { extendedPositionSchemaLegacy } from '../../../common/schemas/legacy/extended-position';
import { visibilityPluginShapeLegacy } from '../../../common/schemas/legacy/visibility';
import { widgetPositionSchemaLegacy } from '../../../common/schemas/legacy/widget-position';

// The subscription-form widget and the payment-form widget share the CRM,
// form-element and form-settings (input/button/additional) data shapes — they
// drive the same `sp-form-*` editor components. Reuse the form widget's
// schemas rather than duplicating them.
import { crmPipelineSchema, crmPipelineSettingSchema, crmPipelineStepSchema } from '../../../form/schemas/legacy/crm';
import { formElementSchema } from '../../../form/schemas/legacy/form-element';
import {
    formAdditionalSettingsSchema,
    formButtonSettingsSchema,
    formInputSettingsSchema,
} from '../../../form/schemas/legacy/form-settings';

import { paymentElementSchema } from './payment-element';
import { paymentViewSchema } from './payment-view';
import { productSettingsSchema, productTypeSchema } from './product';

/**
 * Field shape shared by both placement variants of the payment-form widget —
 * mirrors `PaymentFormWidgetData`
 * (`apps/websites/src/app/widgets/payment-form/type/payment-form-widget.type.ts`)
 * and the defaults produced by `paymentFormData()` in the host model. The
 * variants diverge **only on `position`**: default placement keeps the simple
 * per-device grid, zero placement uses the extended-position grid.
 *
 * Scalars the host factory seeds with `null` are `nullable()`; groups/flags
 * added over the widget's lifetime are `nullish()` (older persisted forms omit
 * them — notably `analyticsSettings`). The schema also normalises what old
 * forms persisted: an absent / `null` `formView` means `'static'`, and the
 * after-payment action is a link (see {@link withLinkAfterPayment}).
 */
const paymentFormCommonDataShape = {
    // CRM integration
    selectedAddressBook: z.number().nullable(),
    selectedPipeline: crmPipelineSchema.nullable(),
    selectedPipelineStep: crmPipelineStepSchema.nullable(),
    selectedPipelineSettings: z.array(crmPipelineSettingSchema).nullable(),
    createAddressBook: z.boolean().nullable(),
    dealName: z.string().nullable(),

    // Tags
    addCrmTags: z.boolean().nullable(),
    contactTags: z.array(z.number()),
    addAddressBookTags: z.boolean().nullable(),
    contactAddressBookTags: z.array(z.number()),

    // Form content & styling
    elements: z.array(formElementSchema),
    inputSettings: formInputSettingsSchema,
    formButtonSettings: formButtonSettingsSchema,
    additionalFormSettings: formAdditionalSettingsSchema,
    analyticsSettings: analyticsSettingsSchemaLegacy.nullish(),

    // Product
    paymentFormName: z.string().nullable(),
    productType: productTypeSchema.nullable(),
    productName: z.string().nullable(),
    productSettings: productSettingsSchema.nullable(),
    paymentElements: z.array(paymentElementSchema),

    // View mode
    formView: paymentViewSchema.nullish().transform((view) => view ?? 'static'),
    isShowModal: z.boolean().nullable(),
    modalTextHint: z.string().nullable(),
    isSynchronizeStyle: z.boolean().nullable(),

    // Visibility
    ...visibilityPluginShapeLegacy,

    /** @deprecated only old forms carry it — see {@link withLinkAfterPayment}. */
    subscriptionMessage: z.string().nullish(),
} as const;

type AfterPaymentData = {
    subscriptionMessage?: string | null;
    additionalFormSettings: {
        subscriptionMessage?: string | null;
        actionAfterConfirmType?: 'message' | 'link' | null;
    };
};

/**
 * A payment form follows a link after payment: unless an old form still carries the root-level
 * `subscriptionMessage`, its message settings are cleared and `actionAfterConfirmType` is `link`.
 * Cleared means nulled where present, not removed, so overlaying the parsed data onto the persisted
 * one cannot bring the message back.
 */
function withLinkAfterPayment<T extends AfterPaymentData>(data: T): T {
    if (data.subscriptionMessage) {
        return data;
    }

    const settings = data.additionalFormSettings;

    return {
        ...data,
        ...('subscriptionMessage' in data ? { subscriptionMessage: null } : {}),
        additionalFormSettings: {
            ...settings,
            ...('subscriptionMessage' in settings ? { subscriptionMessage: null } : {}),
            actionAfterConfirmType: 'link',
        },
    };
}

/**
 * Data shape for a **default-placement** payment-form widget — common fields
 * plus the simple per-device position grid.
 */
export const paymentFormLegacyDefaultDataSchema = z
    .object({
        ...paymentFormCommonDataShape,
        position: widgetPositionSchemaLegacy,
    })
    .overwrite(withLinkAfterPayment);

/**
 * Data shape for a **zero-block-placement** payment-form widget — common fields
 * plus the extended-position grid (per-device width/height/x/y/rotate/zIndex).
 */
export const paymentFormLegacyZeroDataSchema = z
    .object({
        ...paymentFormCommonDataShape,
        position: extendedPositionSchemaLegacy,
    })
    .overwrite(withLinkAfterPayment);
